import { Component, inject, OnInit } from '@angular/core';
import { LdwRouterService } from '../../route/ldw-router.service';
import { DocumentHelper } from '../../shared/helpers/document-helper';
import { LDWDocument } from '../../shared/models/LDWDocument';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DocumentService } from '../../shared/services/document.service';
import { ActivatedRoute } from '@angular/router';
import { InfoService } from '../../shared/services/info.service';
import { UntypedFormGroup } from '@angular/forms';
import { DocumentDetailResolved } from '../../shared/models/document-detail-resolved';
import { DocumentCopyDataResolved } from '../../shared/models/document-copydata-resolved';
import { DocumentCopyDataGroup } from '../../shared/models/document-copydata-group';
import { ErrorDialogComponent } from '../../shared/dialogs/error-dialog/error-dialog.component';
import { CopyDocumentDto } from '../../shared/dto/copy-document-dto';
import { KeyValuePair } from '../../shared/models/key-value-pair';
import {
  TreeNavigationDialogComponent, TreeNavigationValue
} from '../../shared/dialogs/tree-navigation-dialog/tree-navigation-dialog.component';
import { WebDokuTreeNode } from '../../shared/models/webdoku-tree-node';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ViewModelProperty } from '@ldw/roesberg';
import { SettingsService } from '../../shared/settings/settings.service';

type ControlsWithActions = 'DOCUMENT_NAVIGATION_PARENT_ID' | 'BASIC_NAVIGATION_PARENT_ID' | 'OWN_NAVIGATION_PARENT_ID';

@Component({
    selector: 'ldw-document-copy-page',
    templateUrl: './document-copy-page.component.html',
    styleUrls: ['./document-copy-page.component.css'],
    standalone: false
})
export class DocumentCopyPageComponent implements OnInit {
  private routerService = inject(LdwRouterService);
  private documentService = inject(DocumentService);
  private infoService = inject(InfoService);
  private route = inject(ActivatedRoute);
  private settingsService = inject(SettingsService);

  document: LDWDocument | undefined = undefined;
  documentHelper: DocumentHelper;
  properties: DocumentCopyDataGroup[] | [] = [];
  form: UntypedFormGroup = new UntypedFormGroup({});
  captionWidth = 250;


  formControlActions: { [key in ControlsWithActions]: (vmp: ViewModelProperty) => void } = {
    DOCUMENT_NAVIGATION_PARENT_ID: (vmp) => this.treeDoc(vmp).subscribe({
      next: value => this.updateVMP(vmp, value), error: _ => {
        vmp.loading = false;
        this.form.enable();
      }
    }),
    BASIC_NAVIGATION_PARENT_ID: (vmp) =>
      this.showTreeBasicDialog(vmp)
        .subscribe({
          next: valueFromDialog => this.updateVMP(vmp, valueFromDialog), error: _ => {
            vmp.loading = false;
            this.form.enable();
          }
        })
    ,
    OWN_NAVIGATION_PARENT_ID: (vmp) =>
      this.showTreeOwnNavigationDialog(vmp)
        .subscribe({
          next: valueFromDialog => this.updateVMP(vmp, valueFromDialog), error: _ => {
            vmp.loading = false;
            this.form.enable();
          }
        })
  };


  private dialog = inject(MatDialog);
  private translate =  inject(TranslateService);

  constructor() {
    const routerService = this.routerService;

    this.documentHelper = new DocumentHelper(this.dialog, routerService, this.translate);
    this.infoService.clearStatus();
  }

  ngOnInit(): void {

    this.route.data.subscribe((data) => {
      console.log(data);
      this.infoService.clearStatus();
      const documentResolved: DocumentDetailResolved = data['document'];
      const copyDataResolved: DocumentCopyDataResolved = data['copyData'];

      if (documentResolved.documentDetail && copyDataResolved.properties) {
        this.document = documentResolved.documentDetail;
        this.properties = copyDataResolved.properties;
      } else {
        this.error(documentResolved.docId);
      }

      if (documentResolved.error) {
        this.infoService.setStatus(documentResolved.error);
      } else if (copyDataResolved.error) {
        this.infoService.setStatus(copyDataResolved.error);
      }
    });
  }

  openFormControlDialog(vmp: ViewModelProperty) {
    if (!this.formControlActions[vmp.fieldName as ControlsWithActions]) {
      return;
    }
    this.formControlActions[vmp.fieldName as ControlsWithActions](vmp);
  }

  get visibleGroups(): DocumentCopyDataGroup[] | [] {
    return this.properties.filter(x => x.groupName !== '');
  }


  error(docid: string) {
    this.documentHelper.documentNotFound(docid);
  }

  get viewVisible(): boolean {
    return this.document != undefined && this.document.id != '';
  }

  close() {
    this.routerService.navigateBack();
  }

  showToolTip(): boolean {
    return this.settingsService.settings.viewMode.showTooltips;
  }

  public startCopy() {

    if (!this.document?.id) {
      return;
    }

    // prepare data
    const dto = new CopyDocumentDto();
    dto.documentId = this.document.id;
    this.properties.forEach(group => {
      group.fields.forEach(vmp => {
        if (
          vmp != undefined &&
          vmp.fieldName != undefined &&
          vmp.fieldName !== ''
        ) {
          dto.values.push(KeyValuePair.fromValues(vmp.fieldName, vmp.value));
        }
      });
    });

    this.documentService.copyDocument(dto).subscribe((value) => {
      if (value.success) {
        this.close();
      } else {
        this.dialog.open(ErrorDialogComponent, {
          data: {
            title: 'DOCUMENT.DLG_COPYFAILED',
            message: value.message
          }
        });
      }
    });

    /*
     const options = this.getSearchOptionsFromUI();
     if (options.searchValues.length > 0) {
       this.formSubmit.emit(options);
     }
     */
  }

  treeDoc(vmp: ViewModelProperty): Observable<any> {
    vmp.loading = true;
    this.form.disable();
    return this.documentService.getDocumentTree().result$
      .pipe(
        map((result) => result.data),
        filter((value): value is WebDokuTreeNode[] => !!value),
        take(1),
        tap(() => {
          vmp.loading = false;
          this.form.enable();
        }),
        switchMap(value => this.showDialog(this.translate.instant("DOCUMENT.SELECTPOSITIONHEADER"), value, vmp)));
  }

  showTreeBasicDialog(vmp: ViewModelProperty): Observable<any> {
    vmp.loading = true;
    this.form.disable();
    return this.documentService.getBasicTree().result$
      .pipe(
        map((result) => result.data),
        filter((value): value is WebDokuTreeNode[] => !!value),
        take(1),
        tap(() => {
          vmp.loading = false;
          this.form.enable();
        }),
        switchMap(value => this.showDialog(this.translate.instant("DOCUMENT.SELECTPOSITIONHEADER"), value, vmp)));
  }

  showTreeOwnNavigationDialog(vmp: ViewModelProperty): Observable<any> {
    vmp.loading = true;
    this.form.disable();
    return this.documentService.getCustomNavTree().result$
      .pipe(
        map((result) => result.data),
        filter((value): value is WebDokuTreeNode[] => !!value),
        take(1),
        tap(() => {
          vmp.loading = false;
          this.form.enable();
        }),
        switchMap(value => this.showDialog(this.translate.instant("DOCUMENT.SELECTPOSITIONHEADER"), value, vmp)));
  }

  showDialog(header: string, treeData: WebDokuTreeNode[], vmp: ViewModelProperty) {
    return this.dialog.open(TreeNavigationDialogComponent, {
      data: { title: header, treeData: treeData, selectedValue: { id: vmp.value } },
      panelClass: 'dialog-responsive',
      maxWidth: undefined
    }).afterClosed();

  }

  updateVMP(vmp: ViewModelProperty, value: TreeNavigationValue) {
    if (!vmp || !value) {
      return;
    }
    vmp.value = value.id;
    vmp.displayValue = value.displayedText;
  }
}
